/**
 * ============================================================
 *  WEEKLY SHIFT EDITOR – GUIDA COMPONENTI / RESPONSABILITÀ
 * ============================================================
 *
 * Obiettivo:
 *  - Creare un “Turno Settimanale” (weekly working hour compound)
 *  - Per ogni giorno posso:
 *      A) associare un Turno Giornaliero (working_hour)
 *      B) inserire Fasce Manuali (custom time slots)
 *  - Le fasce manuali possono attraversare mezzanotte:
 *      - UI: l’utente inserisce 1 fascia unica (es. 21:00 -> 06:00)
 *      - Stato: viene salvata come 2 pezzi (Lunedì 21:00-24:00, Martedì 00:00-06:00)
 *      - UX: in modale rimane editabile come “fascia unica” (groupId)
 *
 * Struttura dati principale (state):
 *  - daysData: oggetto con chiave = etichetta del giorno ("Lunedì"...)
 *      daysData[dayLabel] può essere:
 *        1) { type: "working_hour", workingHourId, workingHourObj, customSlots: [] }
 *        2) { type: "custom", workingHourId: null, workingHourObj: null, customSlots: CustomSlot[] }
 *
 *  - CustomSlot:
 *      {
 *        id: string,                 // id del pezzo salvato nel giorno
 *        workStartTime: "HH:MM",
 *        workEndTime: "HH:MM" | "24:00",
 *        breaks: BreakItem[],
 *        spillFrom?: "Lunedì" | null // se questo pezzo è la parte “spillata” dal giorno prima
 *        groupId?: string            // id logico fascia unica (per edit/delete “di gruppo”)
 *      }
 *
 * NB: groupId è fondamentale per gestire fasce splittate:
 *  - Se fascia NON attraversa mezzanotte: 1 slot con groupId
 *  - Se attraversa mezzanotte: 2 slot con stesso groupId su due giorni diversi
 *
 *
 * ============================================================
 *  COMPONENTI UI
 * ============================================================
 *
 * 1) CreateWeeklyShiftForm.jsx  (ROOT)
 * ------------------------------------------------------------
 * Responsabilità:
 *  - Contiene lo state principale: daysData, shiftName, selectedOu.
 *  - Carica la lista turni giornalieri (working hours compound) via API.
 *  - Converte daysData in:
 *      - shiftsByDay: dati “render-ready” per WeekCalendar
 *      - whNameByDay: nome turno giornaliero associato al giorno
 *      - isCustomDayByDay: flag per mostrare icona “matita” se giorno custom
 *  - Gestisce:
 *      - open/close modale (BildWeekShiftPreview)
 *      - salvataggio finale (payload -> createWeeklyWorkingHourCompound)
 *
 * Props principali:
 *  - token, selectedOu, allowHiddenBreaks, onSuccess...
 *
 *
 * 2) WeekCalendar.jsx  (VISUALIZZAZIONE SETTIMANA)
 * ------------------------------------------------------------
 * Responsabilità:
 *  - Mostra la tabella 7 giorni con le fasce per giorno.
 *  - Non modifica stato: chiama callback del parent.
 *
 * Props principali:
 *  - daysOfWeek: array giorni (DAYS_LABEL)
 *  - shiftsByDay: oggetto { [dayLabel]: shift[] } pronto da renderizzare
 *  - whNameByDay: oggetto con nome turno giornaliero da tooltip/badge
 *  - readOnly: se true disabilita edit/delete/add
 *  - onPlusCircleClick(day): apre modale sul giorno
 *  - onEditDay(day): apre modale in modalità “custom”
 *  - onCancelShift(day): elimina contenuto giorno (NON deve aprire modale)
 *
 * Note:
 *  - IMPORTANTISSIMO: nei click degli icon (trash/pencil) usare stopPropagation
 *    per evitare che il click “bubbling” attivi altri handler.
 *
 *
 * 3) Buildweekshiftpreview.jsx  (EDITOR DI UN GIORNO)
 * ------------------------------------------------------------
 * Responsabilità:
 *  - UI di modifica per 1 giorno (dayLabel)
 *  - Due tab:
 *      - working_hour: seleziona un turno giornaliero predefinito
 *      - custom: gestisce fasce manuali
 *
 * Stato interno:
 *  - mode: "working_hour" | "custom"
 *  - selectedWorkingHour: item selezionato nel select
 *  - editingGroupId: se presente, sto editando una fascia “logica”
 *  - showEditor/editorKey: controllo rendering del componente TimeSlot
 *
 * Dati visibili:
 *  - visible = getVisiblePiecesForDay(daysData, dayLabel)
 *    -> mostra sia pezzi del giorno, sia eventuali pezzi spillati dal giorno precedente
 *       (a seconda della logica che hai scelto)
 *
 * Importante (stabilità UI):
 *  - NON resettare `mode` ad ogni variazione di daysData mentre la modale è aperta,
 *    altrimenti dopo delete ti torna su “working_hour”.
 *
 * Azioni:
 *  - applyWorkingHourToDay(prev, dayLabel, selectedWorkingHour)
 *  - clearDay(prev, dayLabel)             // svuota giorno + cleanup spill se previsto
 *  - tryUpsertCustomSlot(daysData, anchorDay, rawSlot, editingGroupId/meta)
 *      -> ritorna { ok:true, data } oppure { ok:false, error }
 *      -> NON deve mai throw, così eviti lo “schermo bianco”
 *  - deleteSlotGroup(prev, groupId)
 *
 *
 * 4) TimeSlot.jsx  (FORM FASCIA ORARIA)
 * ------------------------------------------------------------
 * Responsabilità:
 *  - UI per inserire/modificare:
 *      - workStartTime
 *      - workEndTime
 *      - breaks (pause)
 *  - Quando l’utente conferma, emette un rawSlot:
 *      { workStartTime, workEndTime, breaks }
 *
 * Non conosce giorni, groupId, split, ecc.
 *
 *
 * 5) build_breaks_preview.jsx  (UI “chip” pause)
 * ------------------------------------------------------------
 * Responsabilità:
 *  - Dato un array breaks, renderizza “pillole” con orari pause
 *  - Usa normalizeBreaks() per:
 *      - filtrare pause non valide
 *      - ordinare per start_at
 *      - impostare default is_visible/confirmed
 *
 *
 * ============================================================
 *  UTILS – useWeeklyShiftEditor.js
 * ============================================================
 *
 * File di logica centrale: qui devono stare TUTTE le regole “business”.
 *
 * Contiene:
 *  - Costanti:
 *      DAYS_LABEL, DAY_MIN
 *
 *  - Helpers orari:
 *      toMin("HH:MM") -> minuti
 *      toDayTime(min) -> "HH:MM"
 *      isCrossMidnight(start,end)
 *      nextDayLabel(day)
 *
 *  - Breaks:
 *      normalizeBreaks(breaks)
 *      splitBreaksAcrossMidnight(start,end,breaks)
 *
 *  - Validazioni:
 *      validateNoOverlapInDay(dayLabel, daySlots, slotToAdd, ignoreId?)
 *
 *  - Operazioni su daysData:
 *      applyWorkingHourToDay(daysData, dayLabel, workingHourObj)
 *        -> set type=working_hour e pulisce customSlots
 *
 *      clearDay(daysData, dayLabel)
 *        -> rimuove giorno e (se vuoi) rimuove anche pezzi spillati nel giorno successivo
 *
 *      getVisiblePiecesForDay(daysData, dayLabel)
 *        -> prepara “lista display” per la modale:
 *           - includere slots del giorno
 *           - opzionale: includere spill pieces dal giorno precedente/successivo
 *           - ordinare per giorno+ora
 *
 *      getEditingOriginalByGroup(daysData, groupId)
 *        -> ricostruisce fascia “logica” da 1 o 2 pezzi
 *           - se groupId ha 2 pezzi (cross-midnight), ricostruisce endTime reale
 *           - ricompone breaks se necessario
 *
 *      deleteSlotGroup(daysData, groupId)
 *        -> elimina TUTTI i pezzi con quel groupId (anche su giorni diversi)
 *
 *      tryUpsertCustomSlot(daysData, anchorDay, rawSlot, editingMeta)
 *        -> punto unico per:
 *           - generare groupId se nuovo
 *           - se cross-midnight: creare 2 pezzi con stesso groupId
 *           - se edit: rimuovere i vecchi pezzi del groupId e inserirne di nuovi
 *           - validare overlap su ciascun giorno
 *           - ritornare {ok,data} o {ok:false,error} senza throw
 *
 *
 * ============================================================
 *  FLUSSO COMPLETO
 * ============================================================
 *
 * 1) Utente clicca + su un giorno in WeekCalendar
 *    -> CreateWeeklyShiftForm.openDay(day)
 *    -> apre BuildWeekShiftPreviw con dayLabel
 *
 * 2) In BuildWeekShiftPreviw:
 *    - Tab working_hour:
 *        seleziona turno -> applyWorkingHourToDay -> aggiorna daysData
 *
 *    - Tab custom:
 *        inserisce fascia in TimeSlot -> rawSlot
 *        -> tryUpsertCustomSlot(...) aggiorna daysData
 *        -> visible si aggiorna automaticamente
 *
 * 3) WeekCalendar renderizza sempre da shiftsByDay (derivato da daysData)
 *
 * 4) Salvataggio:
 *    buildWeeklyPayload(daysData) -> createWeeklyWorkingHourCompound(payload)
 */


import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import WeekCalendar from "./week_calendar";
import BuildWeekShiftPreview from "./build_week_shift_preview";

import { getWorkingHoursCompound, createWeeklyWorkingHourCompound } from "../services/api_working_hours";
import { DAYS_LABEL, normalizeBreaks, clearDay } from "../utils/useWeeklyShiftEditor";

const dayLabelToWeekDay = (label) => DAYS_LABEL.indexOf(label) + 1;

function clampEnd(hhmm) {
  return hhmm === "24:00" ? "24:00" : hhmm;
}

function toPayloadTimeSlots(slots) {
  return (slots || []).map((s) => ({
    start_at: s.workStartTime,
    end_at: clampEnd(s.workEndTime),
    breaks: normalizeBreaks(s.breaks).map((br) => ({
      start_at: br.start_at,
      end_at: br.end_at,
      is_visible: br.is_visible ?? true,
    })),
  }));
}

function buildWeeklyPayload({ shiftName, selectedOu, daysData }) {
  const slots_by_weekday = DAYS_LABEL.map((dayLabel) => {
    const week_day = dayLabelToWeekDay(dayLabel);
    const dayData = daysData[dayLabel];
    if (!dayData) return null;

    if (dayData.type === "working_hour") {
      return { week_day, working_hour: dayData.workingHourId, time_slots: [] };
    }

    return { week_day, working_hour: null, time_slots: toPayloadTimeSlots(dayData.customSlots || []) };
  })
    .filter(Boolean)
    .filter((d) => d.working_hour || (d.time_slots && d.time_slots.length > 0));

  const payload = { name: shiftName.trim(), ou: selectedOu?.id ?? undefined, slots_by_weekday };
  if (!selectedOu?.id) delete payload.ou;
  return payload;
}

export default function CreateWeeklyShiftForm({
  token,
  selectedOu: selectedOuProp = null,
  allowHiddenBreaks = false,
  onSuccess,
  onWeeklyHasDailyWorkingHoursChange,
  weeklyClearDailyWorkingHoursSignal,
}) {
  const [shiftName, setShiftName] = useState("");
  const [workingHours, setWorkingHours] = useState([]);
  const [loadingWH, setLoadingWH] = useState(false);

  const [daysData, setDaysData] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedOu, setSelectedOu] = useState(selectedOuProp);

  useEffect(() => setSelectedOu(selectedOuProp), [selectedOuProp]);

  // load WH list
  useEffect(() => {
    if (!token) return;
    setLoadingWH(true);

    (async () => {
      try {
        const unitId = selectedOu?.id || null;
        const list = await getWorkingHoursCompound(token, unitId, "shifts");
        if (!selectedOu?.id) setWorkingHours((list || []).filter((w) => !w.unit));
        else setWorkingHours(list || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingWH(false);
      }
    })();
  }, [token, selectedOu?.id]);

  const hasDailyWorkingHours = useMemo(() => {
    return Object.values(daysData).some((d) => d?.type === "working_hour" && d?.workingHourId);
  }, [daysData]);

  useEffect(() => {
    onWeeklyHasDailyWorkingHoursChange?.(hasDailyWorkingHours);
  }, [hasDailyWorkingHours, onWeeklyHasDailyWorkingHoursChange]);

  useEffect(() => {
    if (weeklyClearDailyWorkingHoursSignal == null) return;
    setDaysData((prev) => {
      const next = { ...prev };
      for (const day of Object.keys(next)) {
        if (next[day]?.type === "working_hour") delete next[day];
      }
      return next;
    });
  }, [weeklyClearDailyWorkingHoursSignal]);

  const openDay = (dayLabel) => {
    setSelectedDay(dayLabel);
    setModalOpen(true);
  };
  const closeDay = () => {
    setModalOpen(false);
    setSelectedDay(null);
  };

  const handleCancelShift = (dayLabel) => {
    setDaysData((prev) => clearDay(prev, dayLabel));
  };

  // WeekCalendar data
  const shiftsByDay = useMemo(() => {
    const out = {};
    DAYS_LABEL.forEach((d) => (out[d] = []));

    for (const [dayLabel, cfg] of Object.entries(daysData)) {
      if (!cfg) continue;

      if (cfg.type === "working_hour") {
        const wh = cfg.workingHourObj || workingHours.find((w) => w.id === cfg.workingHourId);
        const timeSlots = wh?.time_slots || [];
        out[dayLabel] = (timeSlots || []).map((ts) => ({
          workStartTime: ts.start_at,
          workEndTime: ts.end_at,
          breaks: normalizeBreaks(ts.breaks || []),
          workingHourName: wh?.name || "",
        }));
      } else if (cfg.type === "custom") {
        out[dayLabel] = (cfg.customSlots || []).map((s) => ({
          ...s,
          breaks: normalizeBreaks(s.breaks || []),
          workingHourName: "",
        }));
      }
    }
    return out;
  }, [daysData, workingHours]);

  const whNameByDay = useMemo(() => {
    const out = {};
    for (const [day, cfg] of Object.entries(daysData)) {
      if (cfg?.type === "working_hour" && cfg.workingHourId) {
        const wh = cfg.workingHourObj || workingHours.find((w) => w.id === cfg.workingHourId);
        if (wh?.name) out[day] = wh.name;
      }
    }
    return out;
  }, [daysData, workingHours]);

  const isCustomDayByDay = useMemo(() => {
    const out = {};
    DAYS_LABEL.forEach((d) => {
      out[d] = daysData?.[d]?.type === "custom" && (daysData?.[d]?.customSlots || []).length > 0;
    });
    return out;
  }, [daysData]);

  const handleSave = async () => {
    if (!shiftName.trim()) {
      return Swal.fire({ icon: "warning", title: "Nome obbligatorio", text: "Inserisci il nome del turno settimanale." });
    }

    const payload = buildWeeklyPayload({ shiftName, selectedOu, daysData });
    if (!payload.slots_by_weekday?.length) {
      return Swal.fire({ icon: "warning", title: "Nessun giorno compilato", text: "Aggiungi almeno un giorno." });
    }

    try {
      Swal.fire({
        title: "Creazione turno settimanale...",
        icon: "info",
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      await createWeeklyWorkingHourCompound(token, payload);

      Swal.close();
      await Swal.fire({ icon: "success", title: "Turno settimanale creato", text: "Creato correttamente." });

      setShiftName("");
      setDaysData({});
      onSuccess?.();
    } catch (error) {
      Swal.close();
      let errorMessage = "Errore durante la creazione del turno settimanale.";
      const data = error?.response?.data;
      if (data) {
        const msgs = [];
        const extract = (v) => {
          if (typeof v === "string") msgs.push(v);
          else if (Array.isArray(v)) v.forEach(extract);
          else if (v && typeof v === "object") Object.values(v).forEach(extract);
        };
        extract(data);
        if (msgs.length) errorMessage = msgs.join(" ");
      }
      Swal.fire({ icon: "error", title: "Errore", text: errorMessage });
    }
  };

  return (
    <div className="mt-4 rounded-b-lg border border-gray-100 bg-gray-100/60 py-4 px-5 space-y-4">
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-md">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold uppercase text-indigo-800">Imposta i giorni</div>
            <div className="text-xs text-gray-500 mt-1">Clicca + su un giorno per associare un turno o creare fasce</div>
          </div>
          {loadingWH ? <div className="text-xs text-gray-500">Caricamento...</div> : null}
        </div>

        <div className="mt-3">
          <WeekCalendar
            daysOfWeek={DAYS_LABEL}
            shiftsByDay={shiftsByDay}
            whNameByDay={whNameByDay}
            readOnly={false}
            onPlusCircleClick={openDay}
            onCancelShift={handleCancelShift}
            onEditDay={(day) => openDay(day)}
            isCustomDayByDay={isCustomDayByDay}
          />
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-md">
        <div className="text-sm font-semibold uppercase text-indigo-800">Nome del turno settimanale</div>
        <div className="text-xs text-gray-500 mt-1">* Inserisci il nome</div>

        <div className="mt-3">
          <input
            value={shiftName}
            onChange={(e) => setShiftName(e.target.value)}
            placeholder="Es. Settimana standard - Magazzino"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 uppercase focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          Salva turno settimanale
        </button>
      </div>

      <BuildWeekShiftPreview
        token={token}
        open={modalOpen}
        dayLabel={selectedDay}
        selectedOu={selectedOu}
        allowHiddenBreaks={allowHiddenBreaks}
        daysData={daysData}
        setDaysData={setDaysData}
        onClose={closeDay}
      />
    </div>
  );
}